"use client";

import { cn } from "@v1/ui/utils";
import { ChevronLeft, X } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactElement, ReactNode } from "react";
import { useMessenger } from "./messenger-context";

export const MESSENGER_ICON_BUTTON_CLASS =
  "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-primary/55 transition-colors hover:bg-action-500/[0.06] hover:text-primary/80";

/** Close button of the window; absent on the full-page variant. */
export function MessengerCloseButton({ className }: { readonly className?: string }): ReactElement | null {
  const t = useTranslations("messenger.header");
  const { close } = useMessenger();
  if (close === null) return null;
  return (
    <button type="button" aria-label={t("schliessen")} onClick={close} className={cn(MESSENGER_ICON_BUTTON_CLASS, className)}>
      <X className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden />
    </button>
  );
}

/** Centered title of a tab view (Chats, Tickets, Hilfe). */
export function MessengerTabHeader({ title }: { readonly title: string }): ReactElement {
  return (
    <header className="relative flex h-14 shrink-0 items-center justify-center bg-background px-12">
      <h2 className="truncate text-[15px] font-semibold text-primary/90">{title}</h2>
      <MessengerCloseButton className="absolute right-3 top-3" />
    </header>
  );
}

/** Header of a detail view: back, optional avatar, title, extra actions, close. */
export function MessengerDetailHeader({
  title,
  subtitle,
  leading,
  actions,
}: {
  readonly title: string;
  readonly subtitle?: ReactNode;
  readonly leading?: ReactNode;
  readonly actions?: ReactNode;
}): ReactElement {
  const t = useTranslations("messenger.header");
  const { nav } = useMessenger();
  return (
    <header className="flex h-14 shrink-0 items-center gap-1.5 bg-background px-2">
      <button type="button" aria-label={t("zurueck")} onClick={nav.back} className={MESSENGER_ICON_BUTTON_CLASS}>
        <ChevronLeft className="h-5 w-5" strokeWidth={2} aria-hidden />
      </button>
      {leading}
      <div className="flex min-w-0 flex-1 flex-col justify-center pl-0.5">
        <h2 className="truncate text-[14px] font-semibold leading-tight text-primary/90">{title}</h2>
        {subtitle ? <div className="mt-0.5 flex min-w-0 items-center gap-1.5">{subtitle}</div> : null}
      </div>
      {actions}
      <MessengerCloseButton />
    </header>
  );
}
